import cloneDeep from 'lodash.clonedeep'
import { navigate } from '@redwoodjs/router'

import { defaultFilters } from '../components/FiltersSiderbar/Filters'
import { ApplicantFiltersActionType } from '../context/applicant-filters.reducer'
import { buildPresetColumns } from './columnUtils'
import {
  getRequiredDataTypesFromPreset,
  getRequiredDataTypesForDefaultPreset,
} from './dataLoadingUtils'

const PAGE_SIZE_URL_PARAM = 'pageSize'
// Fallback results-per-page when a saved view has no stored pageSize.
// Keep in sync with usePageSize(10) in ApplicantsPage.
export const DEFAULT_PAGE_SIZE = 10

/**
 * Transforms the frontend sortBy config into the format expected by the
 * GraphQL preset mutation. Converts `CUSTOM_FIELD_<id>` entries into
 * `{ sortBy: 'CUSTOM_FIELD', customFieldId: '<id>' }`.
 */
export const transformSortByForPreset = (
  sortByConfig: Array<{ sortBy: string; direction: 'ASC' | 'DESC' }>
) => {
  return sortByConfig.map((sort, index) => {
    if (sort.sortBy.startsWith('CUSTOM_FIELD_')) {
      const customFieldId = sort.sortBy.replace('CUSTOM_FIELD_', '')
      return {
        sortBy: 'CUSTOM_FIELD',
        customFieldId,
        direction: sort.direction,
        order: index + 1,
      }
    }
    return {
      sortBy: sort.sortBy,
      direction: sort.direction,
      order: index + 1,
    }
  })
}

// ─── Pure utility functions (unit-testable) ──────────────────────────────────

/**
 * Syncs new custom field keys into originalPresetColumns.visibility so that
 * asynchronously-loaded custom fields don't cause a false-positive
 * `hasUnsavedChanges = true` (Bug 2 fix).
 *
 * Returns the same reference when no update is needed (avoids spurious renders).
 */
export const syncCustomFieldsToOriginalPreset = (
  originalPresetColumns: { visibility: Record<string, boolean>; order: string[] } | null,
  customFieldKeys: string[]
): { visibility: Record<string, boolean>; order: string[] } | null => {
  if (!originalPresetColumns) return originalPresetColumns

  const updates: Record<string, boolean> = {}
  customFieldKeys.forEach((key) => {
    if (!(key in originalPresetColumns.visibility)) {
      updates[key] = false
    }
  })

  if (Object.keys(updates).length === 0) return originalPresetColumns

  return {
    ...originalPresetColumns,
    visibility: { ...originalPresetColumns.visibility, ...updates },
  }
}

/**
 * Reads the results-per-page persisted on a saved view. Page size is stored as a
 * first-class `pageSize` column on the preset (not inside the `filters` JSON, which
 * mirrors ApplicantFiltersInput). Returns null when the view has no stored size.
 */
export const getPresetPageSize = (preset: any): number | null => {
  const pageSize = preset?.pageSize

  return Number.isInteger(pageSize) && pageSize > 0 ? pageSize : null
}

/**
 * Pure computation of whether a preset has unsaved changes.
 * Extracted from `hasUnsavedChanges` useMemo in ApplicantsPage so it can be
 * unit-tested independently.
 */
export const computeHasUnsavedChanges = ({
  selectedPresetId,
  originalPresetFilters,
  filters,
  originalPresetColumns,
  columnVisibility,
  columnOrder,
  originalPresetSortBy,
  sortByConfig,
  originalPresetPageSize,
  pageSize,
}: {
  selectedPresetId: string | null
  originalPresetFilters: any
  filters: any
  originalPresetColumns: { visibility: Record<string, boolean>; order: string[] } | null
  columnVisibility: Record<string, boolean>
  columnOrder: string[]
  originalPresetSortBy: Array<{ sortBy: string; direction: 'ASC' | 'DESC' }> | null
  sortByConfig: Array<{ sortBy: string; direction: 'ASC' | 'DESC' }>
  originalPresetPageSize?: number | null
  pageSize?: number
}): boolean => {
  if (!selectedPresetId) return false

  if (
    originalPresetPageSize !== null &&
    originalPresetPageSize !== undefined &&
    pageSize !== undefined &&
    pageSize !== originalPresetPageSize
  )
    return true

  if (originalPresetFilters) {
    if (JSON.stringify(filters) !== JSON.stringify(originalPresetFilters)) return true
  }

  if (originalPresetColumns) {
    if (
      JSON.stringify(columnVisibility) !==
      JSON.stringify(originalPresetColumns.visibility)
    )
      return true
    if (
      JSON.stringify(columnOrder) !== JSON.stringify(originalPresetColumns.order)
    )
      return true
  }

  if (originalPresetSortBy) {
    if (JSON.stringify(sortByConfig) !== JSON.stringify(originalPresetSortBy))
      return true
  }

  return false
}

// ─────────────────────────────────────────────────────────────────────────────

// Hardcoded list of hiring stages for filter presets
export const defaultHiringStages = [
  { name: 'LEAD' },
  { name: 'APPLICATION' },
  { name: 'QUALIFICATION' },
  { name: 'ONBOARDING' },
]

/**
 * Determines what data types need to be loaded for a given preset
 * @param preset - The preset object (custom or default)
 * @param isCustomPreset - Whether this is a custom preset
 * @returns Object indicating which data types need to be loaded
 */
export const getPresetRequiredDataTypes = (
  preset: any,
  isCustomPreset: boolean
) => {
  if (isCustomPreset) {
    return getRequiredDataTypesFromPreset(preset.filters)
  } else {
    return getRequiredDataTypesForDefaultPreset(preset.name)
  }
}

// Helper to merge loaded options into preset filters
export const mergeOptionsIntoFilters = (presetFilters, loadedOptionsRef) => {
  // Deep clone to avoid readonly property errors
  const mergedFilters = cloneDeep(presetFilters)

  // Merge applicant stages - ensure full structure is present
  if (mergedFilters.applicantStages) {
    // If the filter is missing key metadata fields, restore them from defaultFilters
    if (
      !mergedFilters.applicantStages.key ||
      !mergedFilters.applicantStages.type ||
      !mergedFilters.applicantStages.options
    ) {
      mergedFilters.applicantStages = {
        ...defaultFilters.applicantStages,
        ...mergedFilters.applicantStages,
        key: 'applicantStages',
        type: 'applicant-stage-filter',
        title: 'Applicant Stage',
        options: defaultFilters.applicantStages.options,
      }
    }

    // Update the selected state of options based on selectedValues
    if (mergedFilters.applicantStages.selectedValues?.length > 0) {
      mergedFilters.applicantStages.options =
        mergedFilters.applicantStages.options.map((option) => {
          const hasSelectedSubOption =
            mergedFilters.applicantStages.selectedValues.some(
              (sv) => sv.applicantStage === option.value
            )

          return {
            ...option,
            selected: hasSelectedSubOption,
            options: option.options?.map((subOption) => {
              const isSelected =
                mergedFilters.applicantStages.selectedValues.some((sv) => {
                  if (sv.applicantStage !== option.value) return false
                  return (
                    sv.hiringStage === subOption.value ||
                    sv.disqualifiedReason === subOption.value ||
                    sv.terminatedReason === subOption.value ||
                    sv.notInterestedReason === subOption.value
                  )
                })
              return {
                ...subOption,
                selected: isSelected,
              }
            }),
          }
        })
    }
  }

  // Merge job listings options
  if (mergedFilters.jobListings) {
    if (loadedOptionsRef.current.jobListings.length > 0) {
      mergedFilters.jobListings.options =
        loadedOptionsRef.current.jobListings.map((jobListing) => {
          const isSelected = mergedFilters.jobListings.selectedValues?.includes(
            jobListing.jobListingId
          )
          return {
            value: jobListing.jobListingId,
            label: jobListing.title,
            selected: isSelected,
          }
        })
    } else {
      // If options not loaded yet, keep existing options or use empty array
      mergedFilters.jobListings.options =
        mergedFilters.jobListings.options || []
    }
  }

  // Merge recruiters options
  if (mergedFilters.recruiters) {
    if (loadedOptionsRef.current.recruiters.length > 0) {
      mergedFilters.recruiters.options =
        loadedOptionsRef.current.recruiters.map((recruiter) => {
          const isSelected = mergedFilters.recruiters.selectedValues?.includes(
            recruiter.employeeId
          )
          return {
            value: recruiter.employeeId,
            label: `${recruiter.firstName} ${recruiter.lastName}`,
            selected: isSelected,
          }
        })
    } else {
      // If options not loaded yet, keep existing options or use empty array
      mergedFilters.recruiters.options = mergedFilters.recruiters.options || []
    }
  }

  // Merge tags options
  if (mergedFilters.tags) {
    if (loadedOptionsRef.current.tags.length > 0) {
      mergedFilters.tags.options = loadedOptionsRef.current.tags.map((tag) => {
        const isSelected = mergedFilters.tags.selectedValues?.includes(
          tag.tagId
        )
        return {
          value: tag.tagId,
          label: tag.label,
          selected: isSelected,
        }
      })
    } else {
      // If options not loaded yet, keep existing options or use empty array
      mergedFilters.tags.options = mergedFilters.tags.options || []
    }
  }

  return mergedFilters
}

export const onPresetClick = (
  item,
  isCustomPreset = false,
  {
    isLoadingPreset,
    mergeOptionsIntoFilters,
    dispatchFilters,
    searchTextSet,
    setSelectedPresetId,
    setSelectedPresetFilter,
    loadColumnsFromPreset,
    setSortByConfig,
    navigate,
  }
) => {
  // Mark that we're loading a preset to prevent auto-save
  isLoadingPreset.current = true

  if (isCustomPreset) {
    // Filters added after the view was saved start from their defaults
    const filtersWithOptions = mergeOptionsIntoFilters({
      ...cloneDeep(defaultFilters),
      ...item.filters,
    })

    dispatchFilters({
      type: ApplicantFiltersActionType.FILTERS_CHANGED,
      payload: filtersWithOptions,
    })
    searchTextSet(item.filters.searchText?.searchText || '')

    // Save the preset ID for auto-updates
    setSelectedPresetId(item.applicantFilterPresetId)

    // Add preset ID to URL and remove default view param
    const searchParams = new URLSearchParams(window.location.search)
    searchParams.set('viewId', item.applicantFilterPresetId)
    searchParams.delete('view') // Clear default view param
    // Always set page size when loading a view: use the view's stored size, or
    // fall back to the default. Setting it unconditionally avoids a stale page
    // size carrying over from a previously-loaded view.
    const presetPageSize = getPresetPageSize(item) ?? DEFAULT_PAGE_SIZE
    searchParams.set(PAGE_SIZE_URL_PARAM, presetPageSize.toString())
    navigate(`?${searchParams.toString()}`, { replace: true })

    // Always load columns from preset (will show all if no columns defined)
    loadColumnsFromPreset(item.columns || [])

    // Load sortBy configuration from preset
    if (item.sortBy && item.sortBy.length > 0) {
      // Sort by order field if present
      const sortedSortBy = [...item.sortBy].sort(
        (a, b) => (a.order || 0) - (b.order || 0)
      )
      const formattedSortBy = sortedSortBy.map((s) => ({
        sortBy:
          s.sortBy === 'CUSTOM_FIELD' && s.customFieldId
            ? `CUSTOM_FIELD_${s.customFieldId}`
            : s.sortBy,
        direction: s.direction as 'ASC' | 'DESC',
      }))
      setSortByConfig(formattedSortBy)
    } else {
      // Reset to default sorting if no sortBy in preset
      setSortByConfig([{ sortBy: 'APPLICATION_DATE', direction: 'DESC' }])
    }
  } else {
    // Clear preset ID for default presets
    setSelectedPresetId(null)

    // Add default preset name to URL instead of ID
    const searchParams = new URLSearchParams(window.location.search)
    searchParams.set('view', item.name)
    searchParams.delete('viewId')
    navigate(`?${searchParams.toString()}`, { replace: true })

    dispatchFilters({
      type: ApplicantFiltersActionType.FILTER_BY_HIRING_STAGE_METRIC,
      payload: item,
    })
    // Clear search text when switching to non-custom preset
    searchTextSet('')

    // For default presets, reset to default columns and sorting
    loadColumnsFromPreset([]) // This will show all columns
    setSortByConfig([{ sortBy: 'APPLICATION_DATE', direction: 'DESC' }])
  }
  setSelectedPresetFilter(item.name)

  // Reset the flag after loading is complete
  setTimeout(() => {
    isLoadingPreset.current = false
  }, 100)
}

export const autoUpdatePresetColumns = (
  visibility,
  order,
  selectedPresetId,
  sortByConfig,
  updateFilterPreset
) => {
  if (!selectedPresetId) return

  const columns = buildPresetColumns(order, visibility)
  const sortByWithOrder = transformSortByForPreset(sortByConfig)

  updateFilterPreset({
    variables: {
      applicantFilterPresetId: selectedPresetId,
      input: {
        columns: columns.length > 0 ? columns : [],
        sortBy: sortByWithOrder,
      },
    },
  })
}

export const autoUpdatePresetFilters = (
  selectedPresetId,
  columnOrder,
  columnVisibility,
  sortByConfig,
  filters,
  pageSize,
  ensureCompleteFilterStructure,
  updateFilterPreset
) => {
  if (!selectedPresetId) return

  // Convert columns
  const columns = buildPresetColumns(columnOrder, columnVisibility)

  // Convert sortBy
  const sortByWithOrder = transformSortByForPreset(sortByConfig)

  // Ensure filters have complete structure before saving
  const filtersToSave = ensureCompleteFilterStructure(filters)

  updateFilterPreset({
    variables: {
      applicantFilterPresetId: selectedPresetId,
      input: {
        filters: filtersToSave, // Save the full filter structure, not the GraphQL param version
        pageSize,
        columns: columns.length > 0 ? columns : [],
        sortBy: sortByWithOrder,
      },
    },
  })
}

export const createCustomPresetFromFilters = (
  selectedPresetFilter,
  filterPresets,
  columnOrder,
  columnVisibility,
  sortByConfig,
  pageSize,
  filtersRef,
  ensureCompleteFilterStructure,
  createFilterPreset,
  setSelectedPresetId,
  navigate,
  autoUpdatePresetFilters
) => {
  if (!selectedPresetFilter) return

  // Only auto-create for default views (LEAD, APPLICATION, QUALIFICATION, ONBOARDING)
  const DEFAULT_VIEWS = ['LEAD', 'APPLICATION', 'QUALIFICATION', 'ONBOARDING']
  if (!DEFAULT_VIEWS.includes(selectedPresetFilter)) {
    return
  }

  // Check if preset with this name already exists
  const existingPreset = filterPresets.find(
    (p) => p.name === selectedPresetFilter
  )
  if (existingPreset) {
    // If it exists, switch to it and future changes will update it
    setSelectedPresetId(existingPreset.applicantFilterPresetId)

    // Update URL to use the existing preset's viewId
    const searchParams = new URLSearchParams(window.location.search)
    searchParams.set('viewId', existingPreset.applicantFilterPresetId)
    searchParams.delete('view')
    navigate(`?${searchParams.toString()}`, { replace: true })

    // Trigger update instead
    setTimeout(() => {
      autoUpdatePresetFilters()
    }, 0)
    return
  }

  // Convert columns
  const columns = buildPresetColumns(columnOrder, columnVisibility)

  // Convert sortBy
  const sortByWithOrder = transformSortByForPreset(sortByConfig)

  // Ensure filters have complete structure before saving
  const filtersToSave = ensureCompleteFilterStructure(filtersRef.current)

  // Create new custom preset with same name as default
  createFilterPreset({
    variables: {
      input: {
        name: selectedPresetFilter,
        filters: filtersToSave,
        pageSize,
        columns: columns.length > 0 ? columns : [],
        sortBy: sortByWithOrder.length > 0 ? sortByWithOrder : [],
      },
    },
  })
}
